import { buildControllerRules } from "@gettactic/helpers/src/hook-form/buildControllerRules";
import { buildErrorMessageForName } from "@gettactic/helpers/src/hook-form/buildErrorMessageForName";
import React from "react";
import { Controller, FieldValues } from "react-hook-form";
import { ImageOptionPicker } from "./ImageOptionPicker";
import { HookFormImageOptionPickerProps } from "./ImageOptionPicker.types";

/**
 * Hook-form binding for {@link ImageOptionPicker}. Stores a single option id in
 * single-select mode and an array of ids in multiselect, matching what the
 * select inputs write for the same field types.
 */
export const HookFormImageOptionPicker = <T extends FieldValues>(
  props: HookFormImageOptionPickerProps<T>
): React.ReactElement => {
  const { form, name, rules, onChange, multiselect = false, ...rest } = props;

  return (
    <Controller
      control={form.control}
      name={name}
      rules={buildControllerRules({
        form,
        name,
        rules,
        isRequired: props.isRequired
      })}
      render={({ field, fieldState }) => {
        const selected = multiselect
          ? Array.isArray(field.value)
            ? (field.value as string[])
            : []
          : field.value
            ? [field.value as string]
            : [];

        return (
          <ImageOptionPicker
            {...rest}
            multiselect={multiselect}
            value={selected}
            onChangeValue={(next) => {
              const value = multiselect ? next : (next[0] ?? "");
              field.onChange(value);
              onChange?.(value);
            }}
            error={
              fieldState.error?.message ||
              buildErrorMessageForName(form, name) ||
              undefined
            }
          />
        );
      }}
    />
  );
};
